import { useSortableTable } from './hooks/useSortableTable';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateWithPreference } from './utils/navigationHelper';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function PPMContracts() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  
  // Sorting
  const { items: sortedContracts, requestSort, getSortIndicator } = useSortableTable(contracts, { key: 'created_at', direction: 'desc' });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);

  // Column visibility state - load from API
  const [visibleColumns, setVisibleColumns] = useState({
    contractRef: true,
    contractName: true,
    customer: true,
    startDate: true,
    endDate: true,
    equipmentCount: true,
    annualValue: true,
    paymentTerms: false,
    status: true,
  });

  // Available columns configuration
  const columns = [
    { key: 'contractRef', label: 'Contract Ref' },
    { key: 'contractName', label: 'Contract Name' },
    { key: 'customer', label: 'Customer' },
    { key: 'startDate', label: 'Start Date' },
    { key: 'endDate', label: 'End Date' },
    { key: 'equipmentCount', label: 'Equipment' },
    { key: 'annualValue', label: 'Annual Value' },
    { key: 'paymentTerms', label: 'Payment Terms' },
    { key: 'status', label: 'Status' },
  ];

  useEffect(() => {
    fetchContracts();
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/ppm-contracts');
      const data = await response.json();
      
      if (data.preferences) {
        setVisibleColumns(data.preferences);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async (newPreferences) => {
    try {
      await apiFetch('/api/preferences/default/ppm-contracts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPreferences })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  useEffect(() => {
    savePreferences(visibleColumns);
  }, [visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await apiFetch('/api/ppm/contracts');
      const data = await response.json();
      setContracts(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setLoading(false);
    }
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const filteredContracts = sortedContracts.filter(contract =>
    contract.contract_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.contract_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (value) => {
    if (!value) return '£0.00';
    return `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length + 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading contracts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="PPM Contracts"
        subtitle={`${contracts.length} total`}
        backButton
        onBack={() => navigate('/ppm')}
        backTitle="Back to PPM"
        sticky
        actionButton={
          <button
            onClick={() => navigate('/ppm-contracts/new')}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ New Contract
          </button>
        }
      />

      <main className="max-w-full mx-auto px-4 py-3">
        {/* Search + Column Selector */}
        <div className="mb-3 flex items-center space-x-2">
          <input
            type="text"
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
          />
          
          {/* Column Visibility Button */}
          <div className="relative" ref={columnMenuRef}>
            <button
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 text-black  rounded text-sm font-medium text-black transition flex items-center space-x-1"
            >
              <span>⚙️</span>
              <span>Columns</span>
            </button>

            {showColumnMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-48">
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-600 mb-2 px-2">Show Columns</div>
                  {columns.map(column => (
                    <label
                      key={column.key}
                      className="flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns[column.key]}
                        onChange={() => toggleColumn(column.key)}
                        className="mr-2 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                      />
                      <span className="text-sm text-gray-700">{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded border border-gray-200 ">
          <div className="overflow-auto max-h-[calc(100vh-240px)] relative"><table className="w-full text-sm">
            <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
              <tr>
                {visibleColumns.contractRef && (
                  <th
                    onClick={() => requestSort('contract_ref')}
                    className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-200"
                  >
                    Contract Ref {getSortIndicator('contract_ref')}
                  </th>
                )}
                {visibleColumns.contractName && (
                  <th
                    onClick={() => requestSort('contract_name')}
                    className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-200"
                  >
                    Contract Name {getSortIndicator('contract_name')}
                  </th>
                )}
                {visibleColumns.customer && (
                  <th
                    onClick={() => requestSort('company_name')}
                    className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-200"
                  >
                    Customer {getSortIndicator('company_name')}
                  </th>
                )}
                {visibleColumns.startDate && (
                  <th
                    onClick={() => requestSort('start_date')}
                    className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-200"
                  >
                    Start Date {getSortIndicator('start_date')}
                  </th>
                )}
                {visibleColumns.endDate && (
                  <th
                    onClick={() => requestSort('end_date')}
                    className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-200"
                  >
                    End Date {getSortIndicator('end_date')}
                  </th>
                )}
                {visibleColumns.equipmentCount && (
                  <th
                    onClick={() => requestSort('equipment_count')}
                    className="text-center px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-200"
                  >
                    Equipment {getSortIndicator('equipment_count')}
                  </th>
                )}
                {visibleColumns.annualValue && (
                  <th
                    onClick={() => requestSort('total_annual_value')}
                    className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-200"
                  >
                    Annual Value {getSortIndicator('total_annual_value')}
                  </th>
                )}
                {visibleColumns.paymentTerms && (
                  <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">
                    Payment Terms
                  </th>
                )}
                {visibleColumns.status && (
                  <th
                    onClick={() => requestSort('status')}
                    className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-200"
                  >
                    Status {getSortIndicator('status')}
                  </th>
                )}
                <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredContracts.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumnCount} className="px-3 py-8 text-center text-gray-500">
                    {searchTerm ? 'No contracts match your search' : 'No contracts yet. Click "New Contract" to create one.'}
                  </td>
                </tr>
              ) : (
                filteredContracts.map((contract, index) => (
                  <tr
                    key={contract.id}
                    onClick={() => navigateWithPreference(navigate, `/ppm-contracts/${contract.id}`, 'ppm-contracts')}
                    className={`hover:bg-blue-200 cursor-pointer transition ${
                      index % 2 === 1 ? 'bg-blue-100' : 'bg-white'
                    }`}
                  >
                    {visibleColumns.contractRef && (
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{contract.contract_ref}</div>
                        {contract.renewal_status === 'renewal_due' && (
                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded">
                            Renewal Due
                          </span>
                        )}
                      </td>
                    )}
                    {visibleColumns.contractName && (
                      <td className="px-3 py-2 text-gray-700">{contract.contract_name}</td>
                    )}
                    {visibleColumns.customer && (
                      <td className="px-3 py-2 text-gray-700">{contract.company_name}</td>
                    )}
                    {visibleColumns.startDate && (
                      <td className="px-3 py-2 text-gray-600">{formatDate(contract.start_date)}</td>
                    )}
                    {visibleColumns.endDate && (
                      <td className="px-3 py-2 text-gray-600">{formatDate(contract.end_date)}</td>
                    )}
                    {visibleColumns.equipmentCount && (
                      <td className="px-3 py-2 text-center">
                        <span className="inline-block bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs font-semibold">
                          {contract.equipment_count || 0}
                        </span>
                      </td>
                    )}
                    {visibleColumns.annualValue && (
                      <td className="px-3 py-2 text-right font-medium text-gray-900">
                        {formatCurrency(contract.total_annual_value)}
                      </td>
                    )}
                    {visibleColumns.paymentTerms && (
                      <td className="px-3 py-2 text-gray-600 text-xs">
                        {contract.payment_terms}
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          contract.status === 'active' ? 'bg-green-100 text-green-800' :
                          contract.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                          contract.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                          contract.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {contract.status}
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/ppm-contracts/${contract.id}`);
                        }}
                        className="text-rose-600 hover:text-rose-800 font-medium"
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-600">
          Showing {filteredContracts.length} of {contracts.length}
        </div>
      </main>
    </div>
  );
}

export default PPMContracts;
